import React, { useState, useEffect, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { renderToStaticMarkup } from '@usewaypoint/email-builder';
import { ThemeProvider } from '@mui/material/styles';
import { Alert, AlertDescription } from '../../../ui/alert';

// EmailBuilder.js editor imports
import EditorBlock from '../../../email-builder/documents/editor/EditorBlock.tsx';
import { useDocument, resetDocument } from '../../../email-builder/documents/editor/EditorContext.tsx';
import { createEmailEditorTheme } from '../../../email-builder/theme/muiTheme.js';
import { convertEmailPillsToBrackets } from '../../../../constants/variablePlaceholders';

// Helper function to normalize document for comparison
// This removes undefined values, sorts keys, and creates a consistent structure
// so we can detect when changes are truly reverted
const normalizeDocument = (doc) => {
  if (doc === null || doc === undefined) return null;

  // For arrays, normalize each element
  if (Array.isArray(doc)) {
    return doc.map(normalizeDocument);
  }

  // For objects, recursively normalize and remove undefined values
  if (typeof doc === 'object') {
    const normalized = {};

    // Sort keys for consistent comparison
    const sortedKeys = Object.keys(doc).sort();

    for (const key of sortedKeys) {
      const value = doc[key];

      // Skip undefined values - they're the same as not having the property
      if (value === undefined) continue;

      // Recursively normalize nested objects/arrays
      const normalizedValue = normalizeDocument(value);

      // Only include the property if the normalized value isn't null/undefined
      if (normalizedValue !== null && normalizedValue !== undefined) {
        normalized[key] = normalizedValue;
      }
    }

    return normalized;
  }

  // Primitive values return as-is
  return doc;
};

const EmailTemplateEditor = forwardRef(({ template, name, subject, active, onSubjectChange, onHasChangesChange }, ref) => {
  // Get document from EditorContext (Zustand)
  const document = useDocument();

  // Create MUI theme that matches our shadcn/Tailwind colors
  const muiTheme = useMemo(() => createEmailEditorTheme(), []);

  // Local state
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState({});

  // Track when we're resetting to avoid marking as changed
  const isResettingRef = useRef(false);
  // Track original values to detect real changes
  const originalSubjectRef = useRef(null);
  const originalDocumentRef = useRef(null);
  // Track which template we've initialized for
  const initializedTemplateIdRef = useRef(null);

  // Initialize document when template loads
  useEffect(() => {
    if (!template) return;

    isResettingRef.current = true;

    const initDoc = template?.builder_config || {
      root: {
        type: 'EmailLayout',
        data: {
          backdropColor: '#1a1a1a', // Dark backdrop to match app theme
          canvasColor: '#FFFFFF',   // Keep email canvas white for accurate preview
          textColor: '#242424',     // Email text stays dark (for readability on white canvas)
          fontFamily: 'MODERN_SANS',
          childrenIds: []
        }
      }
    };

    // Pass template ID as documentKey to force StylesPanel remount when template changes
    resetDocument(initDoc, template?.id ? String(template.id) : null);

    // Update original refs when template changes - use template's values, not props
    // This ensures we capture the source of truth from the template object
    originalSubjectRef.current = template.subject || '';
    // Normalize and stringify for consistent comparison
    originalDocumentRef.current = JSON.stringify(normalizeDocument(initDoc));
    initializedTemplateIdRef.current = template?.id;

    // Reset the flag after document update propagates
    setTimeout(() => {
      isResettingRef.current = false;
      setHasChanges(false); // Ensure clean state after load
    }, 100); // Timeout to allow both document and parent state to settle
  }, [template?.id]);

  // Track changes to both subject and document
  useEffect(() => {
    // Don't mark as changed if we're currently resetting
    if (isResettingRef.current || !template) return;

    // Compare current values to originals
    const subjectChanged = subject !== originalSubjectRef.current;

    // Normalize current document before comparing to handle undefined values and property ordering
    const normalizedCurrentDoc = JSON.stringify(normalizeDocument(document));
    const documentChanged = normalizedCurrentDoc !== originalDocumentRef.current;

    // Set hasChanges to true if either changed, false if both match original
    setHasChanges(subjectChanged || documentChanged);
  }, [document, subject, template]);

  // Notify parent when hasChanges updates
  useEffect(() => {
    if (onHasChangesChange) {
      onHasChangesChange(hasChanges);
    }
  }, [hasChanges, onHasChangesChange]);

  // Prepare document for rendering by converting pill HTML to {{variable}} syntax
  const prepareDocumentForSave = (doc) => {
    // Deep clone the document to avoid mutating the original
    const clonedDoc = JSON.parse(JSON.stringify(doc));

    // Process each block in the document
    Object.keys(clonedDoc).forEach(blockId => {
      const block = clonedDoc[blockId];

      // Process Text and Heading blocks that have text/markdown content
      if (block?.type === 'Text' || block?.type === 'Heading') {
        const text = block.data?.props?.text;

        if (text && typeof text === 'string') {
          // Convert pill HTML to {{variable}} syntax using existing function
          block.data.props.text = convertEmailPillsToBrackets(text);
        }
      }
    });

    return clonedDoc;
  };

  // Expose getSaveData and resetChanges methods to parent via ref
  // Parent will call this to get data, then do the API call itself
  useImperativeHandle(ref, () => ({
    getSaveData: () => {
      // Validate required fields - only require subject when template is active
      if (active && !subject?.trim()) {
        throw new Error('Subject line is required for active templates');
      }

      // Prepare document by converting pills to {{variables}} before rendering
      const preparedDocument = prepareDocumentForSave(document);

      // Render prepared document to HTML (now contains {{variables}})
      const body_html = renderToStaticMarkup(preparedDocument, { rootBlockId: 'root' });

      // Extract plain text
      const body_text = body_html.replace(/<[^>]*>/g, '').trim();

      // Return template data for parent to save
      // Uses props from parent (name, subject, active) - NOT from template prop
      return {
        name: name || 'Untitled',
        subject: subject,
        body_html: body_html,
        body_text: body_text,
        builder_config: document,
        template_type: 'builder',
        active: active ?? false
      };
    },
    resetChanges: () => {
      // Called by parent after successful save
      // Update the original refs to current values so future comparisons are accurate
      originalSubjectRef.current = subject;
      originalDocumentRef.current = JSON.stringify(normalizeDocument(document));
      setHasChanges(false);
    }
  }));

  // Warn on unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Error alert */}
      {errors.save && (
        <div className="p-4">
          <Alert variant="destructive">
            <AlertDescription>{errors.save}</AlertDescription>
          </Alert>
        </div>
      )}

      {/* EmailBuilder.js Editor */}
      <div className="flex-1 overflow-auto p-3 bg-[#0a0a0a]">
        <ThemeProvider theme={muiTheme}>
          <div className="max-w-4xl mx-auto">
            <EditorBlock id="root" />
          </div>
        </ThemeProvider>
      </div>
    </div>
  );
});

EmailTemplateEditor.displayName = 'EmailTemplateEditor';

export default EmailTemplateEditor;
